import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import type Mithril from 'mithril';
import SkyspireStarMap from './SkyspireStarMap';
import SkyspireNotArrivedModal from './SkyspireNotArrivedModal';
import { getSkyspireTemplateCode } from '../data/skyspireTemplates';
import { formatSkyspireReachTime, getSkyspireElapsedDays, loadSkyspirePage, searchSkyspirePlayer } from '../services/skyspireApi';
import type { SkyspirePageData, SkyspirePlayer, SkyspireRegion, SkyspireSearchCandidate } from '../services/skyspireApi';

interface SkyspirePageAttrs extends Mithril.Attributes {
  tagId?: string | number;
}

export default class SkyspirePage extends Component<SkyspirePageAttrs> {
  region: SkyspireRegion = 'hk';
  page = 1;
  data: SkyspirePageData | null = null;
  selectedSlot: number | null = null;
  searchTerm = '';
  searchCandidates: SkyspireSearchCandidate[] = [];
  searchDropdownOpen = false;
  searchMessage = '';
  loading = true;
  searching = false;
  loadError = false;
  imageError = false;
  requestId = 0;
  private scaleObserver?: ResizeObserver;

  oninit(vnode: Mithril.Vnode<SkyspirePageAttrs, this>) {
    super.oninit(vnode);
    if (this.attrs.tagId) void this.loadPage('hk', 1);
  }

  private syncCanvasScale(viewport: HTMLElement) {
    const desktopCanvas = !window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    if (!desktopCanvas) {
      viewport.style.removeProperty('--skyspire-scale');
      viewport.style.removeProperty('--skyspire-scaled-height');
      viewport.style.removeProperty('--skyspire-offset');
      viewport.style.removeProperty('--skyspire-image-scale');
      viewport.style.removeProperty('--skyspire-background-top');

      const canvas = viewport.querySelector<HTMLElement>('.SkyspirePage');
      const ranking = viewport.querySelector<HTMLElement>('.SkyspireRanking');
      if (canvas && ranking) {
        // Reserve overflow outside the fixed-ratio canvas so percentage positions stay unchanged.
        const bottomSpace = Math.max(0, Math.ceil(ranking.getBoundingClientRect().bottom - canvas.getBoundingClientRect().bottom));
        viewport.style.setProperty('--skyspire-mobile-bottom-space', `${bottomSpace}px`);
      }
      return;
    }

    viewport.style.removeProperty('--skyspire-mobile-bottom-space');
    const designWidth = 1920;
    const scale = Math.min(1, viewport.clientWidth / designWidth);
    const imageScale = scale > 0 ? 1 / scale : 1;
    const canvas = viewport.querySelector<HTMLElement>('.SkyspirePage');
    const banner = viewport.querySelector<HTMLElement>('.dl_tag_image');
    const canvasHeight = canvas?.scrollHeight ?? 0;
    const bannerHeight = banner?.scrollHeight ?? 0;
    const offset = Math.max(0, (viewport.clientWidth - designWidth * scale) / 2);

    viewport.style.setProperty('--skyspire-scale', scale.toFixed(6));
    viewport.style.setProperty('--skyspire-image-scale', imageScale.toFixed(6));
    viewport.style.setProperty('--skyspire-scaled-height', `${Math.ceil(bannerHeight + canvasHeight * scale)}px`);
    viewport.style.setProperty('--skyspire-offset', `${offset}px`);

    const page = viewport.querySelector<HTMLElement>('.SkyspirePage');
    if (page) {
      const backgroundTop = page.getBoundingClientRect().top - viewport.getBoundingClientRect().top;
      viewport.style.setProperty('--skyspire-background-top', `${Math.max(0, backgroundTop)}px`);
    }
  }

  onupdate(vnode: Mithril.VnodeDOM<SkyspirePageAttrs, this>) {
    if (this.attrs.tagId && !this.data && this.requestId === 0) {
      void this.loadPage('hk', 1);
    }

    const viewport = (vnode.dom as HTMLElement).closest<HTMLElement>('.TagsPage');
    if (viewport) this.syncCanvasScale(viewport);
  }

  oncreate(vnode: Mithril.VnodeDOM<SkyspirePageAttrs, this>) {
    super.oncreate(vnode);
    document.documentElement.classList.add('App--skyspire');
    document.body.classList.add('App--skyspire');

    const viewport = (vnode.dom as HTMLElement).closest<HTMLElement>('.TagsPage');
    if (viewport && typeof ResizeObserver !== 'undefined') {
      this.scaleObserver = new ResizeObserver(() => {
        this.syncCanvasScale(viewport);
      });
      this.scaleObserver.observe(viewport);
      const ranking = viewport.querySelector<HTMLElement>('.SkyspireRanking');
      if (ranking) this.scaleObserver.observe(ranking);
    }
    if (viewport) this.syncCanvasScale(viewport);
  }

  onremove() {
    this.requestId += 1;
    this.scaleObserver?.disconnect();
    this.scaleObserver = undefined;
    document.documentElement.classList.remove('App--skyspire');
    document.body.classList.remove('App--skyspire');
  }

  assetPicture(name: string, className: string, mobileName = `${name}_ph`) {
    const assetRoot = `${app.data.pkgLogo.cdnUrl}/${app.data.pkgLogo.ossFile}`;

    return (
      <picture className={`${className}-picture`}>
        <source media="(max-width: 767px) and (hover: none) and (pointer: coarse)" srcset={`${assetRoot}/${mobileName}.png`} />
        <img
          className={className}
          src={`${assetRoot}/${name}.png`}
          alt=""
          aria-hidden="true"
          onerror={() => {
            this.imageError = true;
            m.redraw();
          }}
        />
      </picture>
    );
  }

  async loadPage(region: SkyspireRegion, page: number, selectedSlot: number | null = null) {
    const requestId = ++this.requestId;
    this.loading = true;
    this.loadError = false;
    m.redraw();

    try {
      if (!this.attrs.tagId) throw new Error('Skyspire tagId is missing');
      const data = await loadSkyspirePage({ region, page, tagId: this.attrs.tagId });
      if (requestId !== this.requestId) return;

      this.region = data.region;
      this.page = data.page;
      this.data = data;
      const firstSlot = data.players.findIndex((player) => player.rid !== null);
      this.selectedSlot = firstSlot === -1 ? null : Math.min(selectedSlot === null ? firstSlot : selectedSlot, data.players.length - 1);
    } catch (error) {
      if (requestId !== this.requestId) return;
      this.loadError = true;
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
        m.redraw();
      }
    }
  }

  changeRegion(region: SkyspireRegion) {
    if (this.loading || region === this.region) return;
    this.searchMessage = '';
    this.searchCandidates = [];
    this.searchDropdownOpen = false;
    void this.loadPage(region, 1);
  }

  changePage(offset: number) {
    if (!this.data || this.loading) return;

    const targetPage = this.page + offset;
    if (targetPage < 1 || targetPage > this.data.totalPages) return;

    this.searchMessage = '';
    this.searchCandidates = [];
    this.searchDropdownOpen = false;
    void this.loadPage(this.region, targetPage);
  }

  async selectSearchCandidate(candidate: SkyspireSearchCandidate) {
    this.searchCandidates = [];
    this.searchDropdownOpen = false;
    this.searchTerm = candidate.player.name || this.searchTerm;
    await this.loadPage(candidate.region, candidate.page, candidate.slot);
  }

  async search() {
    if (this.searching || !this.searchTerm.trim()) return;

    this.searching = true;
    this.searchMessage = '';
    m.redraw();

    try {
      if (!this.attrs.tagId) throw new Error('Skyspire tagId is missing');
      const result = await searchSkyspirePlayer(this.searchTerm, this.attrs.tagId, this.region);

      if (!result.candidates.length) {
        this.searchMessage = '';
        this.searchCandidates = [];
        this.searchDropdownOpen = false;
        app.modal.show(SkyspireNotArrivedModal);
        return;
      }

      if (result.candidates.length === 1) {
        await this.selectSearchCandidate(result.candidates[0]);
        return;
      }

      this.searchCandidates = result.candidates;
      this.searchDropdownOpen = true;
    } catch (error) {
      this.searchMessage = String(app.translator.trans('dl-skyspire.forum.load_failed'));
    } finally {
      this.searching = false;
      m.redraw();
    }
  }

  regionLabel(region: SkyspireRegion) {
    return app.translator.trans(`dl-skyspire.forum.region_${region}`);
  }

  reachTimeLabel(player: SkyspirePlayer): string {
    return formatSkyspireReachTime(player.reachTime, player.region);
  }

  durationLabel(player: SkyspirePlayer) {
    const days = getSkyspireElapsedDays(player.reachDurationSeconds);
    if (days === null) return '';

    return app.translator.trans('dl-skyspire.forum.days', { days });
  }

  selectedPlayer(): SkyspirePlayer | null {
    if (!this.data || this.selectedSlot === null) return null;
    const player = this.data.players[this.selectedSlot];
    return player && player.rid !== null ? player : null;
  }

  renderControls() {
    const assetRoot = `${app.data.pkgLogo.cdnUrl}/${app.data.pkgLogo.ossFile}`;
    const pageSize = this.data?.pageSize || 10;
    const start = (this.page - 1) * pageSize + 1;
    const end = start + pageSize - 1;
    const previousDisabled = this.loading || this.page <= 1;
    const nextDisabled = this.loading || !this.data || this.page >= this.data.totalPages;
    const regionBackground = this.region === 'hk' ? 'tqqxx_6' : 'tqqxx_6_1';

    return (
      <div className="SkyspireControls">
        <div className="SkyspireControl SkyspireControl--regions">
          {this.assetPicture(regionBackground, 'SkyspireControl-background')}
          <div className="SkyspireRegions">
            {(['hk', 'global'] as SkyspireRegion[]).map((region) => (
              <button
                type="button"
                className={region === this.region ? 'is-active' : ''}
                aria-pressed={region === this.region ? 'true' : 'false'}
                onclick={() => this.changeRegion(region)}
              >
                {this.regionLabel(region)}
              </button>
            ))}
          </div>
        </div>
        <form
          className="SkyspireControl SkyspireControl--search"
          onsubmit={(event: SubmitEvent) => {
            event.preventDefault();
            void this.search();
          }}
        >
          {this.assetPicture('tqqx_4ss', 'SkyspireControl-background', 'tqqx_4ss')}
          <input
            type="search"
            value={this.searchTerm}
            placeholder={String(app.translator.trans('dl-skyspire.forum.search_placeholder'))}
            aria-label={String(app.translator.trans('dl-skyspire.forum.search_placeholder'))}
            oninput={(event: InputEvent) => {
              this.searchTerm = (event.target as HTMLInputElement).value;
              this.searchMessage = '';
              this.searchCandidates = [];
              this.searchDropdownOpen = false;
            }}
            onfocus={() => {
              if (this.searchCandidates.length > 1) {
                this.searchDropdownOpen = true;
              }
            }}
          />
          <button
            type="submit"
            className="SkyspireSearch-submit"
            disabled={this.searching || !this.searchTerm.trim()}
            aria-label={String(app.translator.trans('dl-skyspire.forum.search_action'))}
          ></button>
          {this.searchDropdownOpen && this.searchCandidates.length > 1 && (
            <div className="SkyspireSearch-results" role="listbox">
              {this.searchCandidates.map((candidate) => (
                <button type="button" className="SkyspireSearch-result" role="option" onclick={() => void this.selectSearchCandidate(candidate)}>
                  <span className="SkyspireSearch-result-name">
                    {candidate.player.server} · {candidate.player.name}
                  </span>
                  {/* <span className="SkyspireSearch-result-region">{this.regionLabel(candidate.region)}</span> */}
                  <img className="SkyspireSearch-result-region" src={`${assetRoot}/tqqx_ss.png`} alt="" aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
        </form>
        <div className="SkyspireControl SkyspireControl--pager">
          {this.assetPicture('tqqx_5', 'SkyspireControl-background')}
          <button
            type="button"
            className="SkyspirePager-button SkyspirePager-button--previous"
            disabled={previousDisabled}
            aria-label={String(app.translator.trans('dl-skyspire.forum.previous_page'))}
            onclick={() => this.changePage(-1)}
          ></button>
          <span className="SkyspirePager-label">{app.translator.trans('dl-skyspire.forum.pagination', { page: this.page, start, end })}</span>
          <button
            type="button"
            className="SkyspirePager-button SkyspirePager-button--next"
            disabled={nextDisabled}
            aria-label={String(app.translator.trans('dl-skyspire.forum.next_page'))}
            onclick={() => this.changePage(1)}
          ></button>
        </div>
        {this.searchMessage && (
          <p className="SkyspireSearch-message" role="status">
            {this.searchMessage}
          </p>
        )}
      </div>
    );
  }

  renderDetail(player: SkyspirePlayer | null) {
    return (
      <section className="SkyspireDetail" aria-label={String(app.translator.trans('dl-skyspire.forum.player_detail'))}>
        {this.assetPicture('tqqx_1', 'SkyspireDetail-background')}
        {player ? (
          <div className="SkyspireDetail-content">
            <h2>{player.name}</h2>
            <dl>
              <div>
                <dt>{app.translator.trans('dl-skyspire.forum.region')}</dt>
                <dd>{this.regionLabel(player.region)}</dd>
              </div>
              <div>
                <dt>{app.translator.trans('dl-skyspire.forum.server')}</dt>
                <dd>{player.server}</dd>
              </div>
              <div>
                <dt>{app.translator.trans('dl-skyspire.forum.arrival_date')}</dt>
                <dd>{this.reachTimeLabel(player)}</dd>
              </div>
              <div>
                <dt>{app.translator.trans('dl-skyspire.forum.elapsed_time')}</dt>
                <dd>{this.durationLabel(player)}</dd>
              </div>
            </dl>
          </div>
        ) : (
          <p className="SkyspireDetail-empty">{app.translator.trans('dl-skyspire.forum.waiting_for_arrivals')}</p>
        )}
      </section>
    );
  }

  renderLatest(player: SkyspirePlayer | null) {
    return (
      <div className="SkyspireLatest">
        <span>{app.translator.trans('dl-skyspire.forum.newest_star')}：</span>
        <strong>{player ? `${player.server} · ${player.name}` : app.translator.trans('dl-skyspire.forum.waiting_for_arrivals')}</strong>
      </div>
    );
  }

  renderRanking(players: SkyspirePlayer[]) {
    const rows = players.length ? players : Array.from({ length: 10 }, () => null);

    return (
      <section className="SkyspireRanking" aria-label={String(app.translator.trans('dl-skyspire.forum.title'))}>
        {this.assetPicture('tqqxx_2', 'SkyspireRanking-background')}
        <div className="SkyspireRanking-content">
          <h2>{app.translator.trans('dl-skyspire.forum.title')}</h2>
          <p>{app.translator.trans('dl-skyspire.forum.ranking_description')}</p>
          <div className="SkyspireRanking-row SkyspireRanking-row--header">
            <span>{app.translator.trans('dl-skyspire.forum.server')}</span>
            <span>{app.translator.trans('dl-skyspire.forum.region')}</span>
            <span>{app.translator.trans('dl-skyspire.forum.character_name')}</span>
            <span>{app.translator.trans('dl-skyspire.forum.arrival_date')}</span>
            <span>{app.translator.trans('dl-skyspire.forum.elapsed_time')}</span>
          </div>
          {rows.map((player, index) => (
            <div className="SkyspireRanking-row" key={player?.id || `empty-${index}`}>
              <span>{player?.server || ''}</span>
              <span>{player ? this.regionLabel(player.region) : ''}</span>
              <span title={player?.name}>{player?.name || ''}</span>
              <span title={player ? this.reachTimeLabel(player) : ''}>{player ? this.reachTimeLabel(player) : ''}</span>
              <span title={player ? String(this.durationLabel(player)) : ''}>{player ? this.durationLabel(player) : ''}</span>
            </div>
          ))}
        </div>
      </section>
    );
  }

  view() {
    const assetRoot = `${app.data.pkgLogo.cdnUrl}/${app.data.pkgLogo.ossFile}`;
    const players = this.data?.players || [];
    const selectedPlayer = this.selectedPlayer();
    const templateCode = getSkyspireTemplateCode(this.page);

    return (
      <div className="SkyspirePageViewport">
        <div
          className={`SkyspirePage${this.loading ? ' is-loading' : ''}${this.loadError ? ' has-error' : ''}${
            this.imageError ? ' has-image-error' : ''
          }`}
        >
          <picture className="SkyspireDividerCloudPhone" aria-hidden="true">
            <img src={`${assetRoot}/countdown-yun-ph.png`} alt="" />
          </picture>
          {this.renderControls()}
          <SkyspireStarMap
            assetRoot={assetRoot}
            templateCode={templateCode}
            players={players}
            selectedSlot={this.selectedSlot}
            onSelect={(slot) => {
              this.selectedSlot = slot;
            }}
          />
          {this.renderDetail(selectedPlayer)}
          {this.renderLatest(this.data?.latestPlayer || null)}
          {this.renderRanking(players)}
          {this.loading && (
            <div className="SkyspireState" role="status">
              {app.translator.trans('dl-skyspire.forum.loading')}
            </div>
          )}
          {this.loadError && (
            <div className="SkyspireState SkyspireState--error" role="alert">
              {app.translator.trans('dl-skyspire.forum.load_failed')}
            </div>
          )}
          {!this.loading && !this.loadError && !players.length && (
            <div className="SkyspireState" role="status">
              {app.translator.trans('dl-skyspire.forum.waiting_for_arrivals')}
            </div>
          )}
        </div>
      </div>
    );
  }
}
